import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { LTTSelector } from '@state/global/ltt.selectors';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatDialog } from '@angular/material/dialog';
import { NavigationService } from '@services/nav/nav.service';
import { ICON } from '@core/util/icon';
import { DirectionOfFeesOptions } from '@core/util/directionOfFees';
import { cloneDeep } from 'lodash';
import { LandTransferTax, LTTExecutedBy, LTTRate, MLTT } from '@models/matter';
import {
	SelectionGroup,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { SalePriceDialogComponent } from '../adjustments/sale-price-dialog';
import { LTTSettingsDialogComponent } from './lttsettings/lttsettings-dialog.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';

export class LttViewModel {
	private store = inject(Store);
	private matterDispatch = inject(MatterDispatch);
	private analytics = inject(AnalyticsService);
	private dialog = inject(MatDialog);
	private route = inject(NavigationService);

	readonly ICON = ICON;
	readonly directionOfFeesOptions = DirectionOfFeesOptions;

	readonly FriendlyLTTExecutedBy = {
		[LTTExecutedBy.CLIENT]: 'Client',
		[LTTExecutedBy.SOLICITOR]: 'Solicitor',
	};

	readonly FriendlyLTTRate = {
		[LTTRate.RESIDENTIAL]: 'Residential',
		[LTTRate.COMMERCIAL]: 'Commercial',
		[LTTRate.APPORTION]: 'Apportion',
	};

	matterId = this.store.selectSnapshot(SelectedMatter.id);

	readonly lttCalculated = toSignal(
		this.store.select(LTTSelector.lttCalculated),
	);

	ltt = computed(() =>
		this.lttCalculated()?.tax ? cloneDeep(this.lttCalculated()?.tax) : null,
	);

	matterClients = this.store.selectSignal(
		MatterClients.get_signorsWithAssociationMapped,
	);
	showLroIsOtherMessage = this.store.selectSignal(
		LTTSelector.showLroIsOtherMessage,
	);
	showLroIsTorontoMessage = this.store.selectSignal(
		LTTSelector.showLroIsTorontoMessage,
	);

	numberOfFirstTimeHomeBuyers = computed(() => {
		const clients = this.matterClients();
		return Object.values(clients).filter(
			(client) => client.firstTimeHomeBuyer === true,
		).length;
	});

	affidavitItems: SelectionGroup<{ value: LTTExecutedBy; name: string }>[] = [
		{
			items: [
				{
					value: LTTExecutedBy.CLIENT,
					name: this.FriendlyLTTExecutedBy[LTTExecutedBy.CLIENT],
				},
				{
					value: LTTExecutedBy.SOLICITOR,
					name: this.FriendlyLTTExecutedBy[LTTExecutedBy.SOLICITOR],
				},
			],
		},
	];

	rateItems: SelectionGroup<{ value: LTTRate; name: string }>[] = [
		{
			items: [
				{
					value: LTTRate.RESIDENTIAL,
					name: 'Residential',
				},
				{
					value: LTTRate.COMMERCIAL,
					name: 'Commercial',
				},
				{
					value: LTTRate.APPORTION,
					name: 'Apportion',
				},
			],
		},
	];

	onSelectExecutedBy(selected: DropdownResult<{ value: LTTExecutedBy }>) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			executedBy: selected.result.value,
		});
	}

	setRate(value: DropdownResult<{ value: LTTRate }>) {
		this.matterDispatch.UpdateLtt(this.matterId, { rate: value.result.value });
	}

	setResidenceValue(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, { residenceValue: value });
	}

	setLandUse(value: string) {
		this.matterDispatch.UpdateLtt(this.matterId, { landUse: value });
	}

	setConfirmationNumber(value: string) {
		this.matterDispatch.UpdateLtt(this.matterId, { confirmationNumber: value });
	}

	setApplyNRST(value: boolean) {
		const currentLtt = this.ltt();
		this.matterDispatch.UpdateLtt(this.matterId, {
			applyNRST: value,
			applyMNRST:
				value && currentLtt?.mltt?.applied ? true : currentLtt?.applyMNRST,
		});
	}

	setNrstPercentageClaimed(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			nrstPercentageClaimed: value,
		});
	}

	setNrstReceiptNumber(value: string) {
		this.matterDispatch.UpdateLtt(this.matterId, { nrstReceiptNumber: value });
	}

	setApplyChattels(value: boolean) {
		this.matterDispatch.UpdateLtt(this.matterId, { applyChattels: value });
	}

	setChattelsValue(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, { chattelsValue: value });
	}

	setChattelConsiderations(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			chattelsConsiderations: value,
		});
	}

	setMlttApplied(value: boolean) {
		const currentLtt = this.ltt();
		this.matterDispatch.UpdateLtt(this.matterId, {
			mltt: {
				applied: value,
			},
			applyMNRST:
				value && currentLtt?.applyNRST ? true : currentLtt?.applyMNRST,
		});
	}

	setMlttAdminFee(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			mltt: { adminFee: value } as MLTT,
		});
	}

	setMlttApplyHst(value: boolean) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			mltt: { applyHST: value } as MLTT,
		});
	}

	setMlttPercentageClaimed(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			mltt: { percentageClaimed: value } as MLTT,
		});
	}

	setMlttApplyMnrst(value: boolean) {
		this.matterDispatch.UpdateLtt(this.matterId, { applyMNRST: value });
	}

	setMnrstPercentageClaimed(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			mnrstPercentageClaimed: value,
		});
	}

	setMnrstReceiptNumber(value: string) {
		this.matterDispatch.UpdateLtt(this.matterId, { mnrstReceiptNumber: value });
	}

	setAssumedMortgage(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, { assumedMortgage: value });
	}

	setPropertyTransferred(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			propertyTransferred: value,
		});
	}

	setConsiderations(value: number) {
		this.matterDispatch.UpdateLtt(this.matterId, { considerations: value });
	}

	setApplyLttRefund(value: boolean) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			applyLTTRefund: value,
			mltt: { applyMLTTRefund: value } as MLTT,
		});
	}

	setRefundPercentageClaimed(value: number) {
		if (value > 100 || value <= 0) {
			return;
		}
		this.matterDispatch.UpdateLtt(this.matterId, {
			refundPercentageClaimed: value,
			mltt: { percentageClaimed: value } as MLTT,
		});
	}

	setMoniesPaid(value: number | null) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			moniesPaid: value,
		} as LandTransferTax);
	}

	setReturnedToVendor(value: number | null) {
		this.matterDispatch.UpdateLtt(this.matterId, {
			returnedToVendor: value,
		} as LandTransferTax);
	}

	editSalePrice(): void {
		this.dialog.open(SalePriceDialogComponent, {
			data: {
				backString: 'Considerations & LTT',
			},
			autoFocus: true,
			maxHeight: '100%',
			maxWidth: '900px',
			width: '90%',
		});
	}

	goToPropertyDetails(): void {
		this.route.goToMatter_Property(this.matterId, {});
	}

	openSettings(): void {
		this.analytics.logEvent(
			AnalyticsEvent.LTT_PAGE_EDIT_FINANCE_SETTINGS_CLICK,
		);
		this.dialog.open(LTTSettingsDialogComponent);
	}
}
